import { useState } from "react";

import { Plus } from "lucide-react";

import { useTableSort } from "@/components/helpers/custom-hooks/use-table-sort";
import {
  isInWorkspace,
  isWorkspaceAdmin,
} from "@/components/helpers/workspace/permissions";
import { useAuth } from "@/components/lib/auth";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { ModifyProgramDialogs } from "@/components/pages_logged_in/program/components/ModifyProgramDialogs";
import { ProgramHeader } from "@/components/pages_logged_in/program/components/ProgramHeader";
import { ProgramTable } from "@/components/pages_logged_in/program/components/ProgramTable";
import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import { PermissionErrorPage } from "@/components/pages_public/403/PermissionError";
import { ProgramIntro } from "@/components/pieces/programs/ProgramIntro";
import { Button } from "@/components/ui/button";
import { Filter } from "@/components/ui/filter";
import { SearchInput } from "@/components/ui/search-input";
import {
  ProgramStatus,
  useFeatureIntroducedQuery,
  useProgramsQuery,
} from "@/graphql";
import {
  MiniProgram,
  ProgramDialog,
  ProgramTableField,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

const FETCH_LIMIT = 10;

const PROGRAM_STATUS_FILTER_OPTIONS = [
  { value: ProgramStatus.InDesign, label: "In Design" },
  { value: ProgramStatus.LaunchReady, label: "Launch Ready" },
  { value: ProgramStatus.Live, label: "Live" },
  { value: ProgramStatus.Complete, label: "Complete" },
];

function Programs() {
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState<ProgramDialog>();
  const [selectedProgram, setSelectedProgram] = useState<MiniProgram>();
  const [query, setQuery] = useState<string>();
  const isAdmin = isWorkspaceAdmin();
  const { currentWorkspace } = useAuth();

  const [selectedProgramStatuses, setSelectedProgramStatuses] = useState<
    ProgramStatus[]
  >(PROGRAM_STATUS_FILTER_OPTIONS.map((o) => o.value));

  // Check if user has been introduced to the program feature
  const { data: featureIntroData } = useFeatureIntroducedQuery({
    variables: { featureType: "PROGRAM_INTRO" },
  });

  // Sorting
  const { currentSort, updateSortState, getOrderField } =
    useTableSort<ProgramTableField>("programListSort", {
      field: "name",
      asc: true,
    });

  const {
    loading,
    data: queryData,
    refetch: refetchPrograms,
  } = useProgramsQuery({
    variables: {
      page: currentPage,
      size: FETCH_LIMIT,
      orderField: getOrderField(),
      status: selectedProgramStatuses,
      query,
    },
    skip: selectedProgramStatuses.length === 0,
  });

  const totalRecords = queryData?.programs?.total || 0;
  const programs = ensureNonNullArray(queryData?.programs?.data);

  const onClosedDialog = () => {
    setDialogOpen(undefined);
    setSelectedProgram(undefined);
  };

  const onSort = (field: ProgramTableField) => {
    setCurrentPage(1);
    updateSortState(field);
  };

  const onStatusFilterChange = (options: ProgramStatus[]) => {
    setSelectedProgramStatuses(options);
    setCurrentPage(1);
  };

  if (!isInWorkspace() || !currentWorkspace.programsEnabled) {
    return <PermissionErrorPage />;
  }

  const hasPrograms = programs.length > 0;
  const shouldShowProgramIntro =
    featureIntroData && !featureIntroData.featureIntroduced;

  return (
    <div className="ml-0 md:ml-sidebar">
      <Nav />
      <ProgramHeader />
      <div className="mx-auto px-4 w-full xl:max-w-content mb-2">
        {!loading && shouldShowProgramIntro && (
          <div className="my-6">
            <ProgramIntro
              show={true}
              onCreateProgram={() => setDialogOpen("add-programs")}
            />
          </div>
        )}

        <div className="w-full my-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-full md:w-80">
            <SearchInput
              value={query || ""}
              onChange={(value) => {
                setQuery(value);
              }}
              placeholder="Search Programs..."
              loading={false}
            />
          </div>
          <div className="flex justify-between flex-1 items-center">
            <Filter
              variant="multiple"
              label="Status"
              size="lg"
              values={selectedProgramStatuses}
              onChange={onStatusFilterChange}
              options={PROGRAM_STATUS_FILTER_OPTIONS}
            />
            {isAdmin && (
              <Button
                intent="primary"
                size="md"
                onClick={() => {
                  setDialogOpen("add-programs");
                }}
              >
                <Plus />
                Create Program
              </Button>
            )}
          </div>
        </div>
        <div className="w-full py-1 bg-white rounded-lg border">
          <ProgramTable
            programs={programs}
            onUpdateProgram={(program) => {
              setSelectedProgram(program);
              setDialogOpen("update-program");
            }}
            onArchiveProgram={(program) => {
              setSelectedProgram(program);
              setDialogOpen("archive-program");
            }}
            onEndProgram={(program) => {
              setSelectedProgram(program);
              setDialogOpen("end-program");
            }}
            onCloneProgram={(program) => {
              setSelectedProgram(program);
              setDialogOpen("clone-program");
            }}
            onSort={onSort}
            currentSort={currentSort}
            loading={loading}
          />
          {hasPrograms && (
            <Paging
              style={{ background: "#ffffff", padding: 10, borderRadius: 5 }}
              currentPage={currentPage}
              pageSize={FETCH_LIMIT}
              setCurrentPage={(index) => {
                setCurrentPage(index);
              }}
              totalRecords={totalRecords}
              isSticky={true}
            />
          )}
        </div>
      </div>
      {dialogOpen && (
        <ModifyProgramDialogs
          dialogOpen={dialogOpen}
          selectedProgram={selectedProgram}
          refetch={refetchPrograms}
          onClosedDialog={onClosedDialog}
        />
      )}
    </div>
  );
}

export function ProgramListPage() {
  return (
    <Authenticate maxWidth="100%" layout="full">
      <Programs />
    </Authenticate>
  );
}
